/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Message, Segment } from '@mairistem/composants';
import { AnnotationSectionHeader } from '@mairistem/administres-utils';

/** Largeur de repli des vignettes, avant mesure de la colonne. */
const LARGEUR_MINIMALE = 150;

/** Marges cumulées de la colonne et de la carte qui porte la vignette. */
const MARGE_VIGNETTE = 28;

interface VignetteProps {
  /** Page telle que rendue dans l'aperçu. */
  section: HTMLElement;
  /** Largeur disponible dans la colonne. */
  largeur: number;
}

/**
 * Réduction d'une page rendue par `docx-preview`.
 *
 * La page est clonée puis mise à l'échelle en CSS : c'est le même DOM que
 * l'aperçu, donc la vignette est fidèle sans passer par un rendu en image.
 *
 * Le clone est replacé sous un `div.docx-apercu-wrapper` : les styles du
 * document, injectés par `docx-preview`, sont préfixés par ce sélecteur — hors
 * de lui, la page perdrait toute sa mise en forme.
 */
const Vignette = (props: VignetteProps) => {
  const { section, largeur } = props;

  const hote = React.useRef<HTMLDivElement>(null);
  const [hauteur, setHauteur] = React.useState(0);

  React.useEffect(() => {
    const cible = hote.current;
    if (!cible) return;

    cible.replaceChildren();

    const { width, height } = section.getBoundingClientRect();
    // Avant la mise en page du navigateur, la largeur est nulle : mieux vaut
    // ne rien afficher qu'une vignette à l'échelle 1.
    if (!width) return;

    const echelle = largeur / width;

    /*
      La largeur de la page est reportée explicitement et la disposition en
      colonne flex de `docx-preview` neutralisée : sans cela la page, devenue
      élément flexible d'un conteneur étroit, rétrécit à la largeur de la
      vignette — son texte se replie — *avant* d'être réduite. La vignette
      montrait alors une mise en page qui n'était pas celle du document.
    */
    const enveloppe = window.document.createElement('div');
    enveloppe.className = 'docx-apercu-wrapper';
    enveloppe.style.cssText = [
      'display: block',
      `width: ${width}px`,
      'padding: 0',
      'background: none',
      `transform: scale(${echelle})`,
      'transform-origin: top left',
    ].join('; ');

    const clone = section.cloneNode(true) as HTMLElement;
    clone.style.flex = 'none';
    clone.style.width = `${width}px`;
    clone.style.margin = '0';
    clone.style.boxShadow = 'none';
    enveloppe.appendChild(clone);

    cible.appendChild(enveloppe);
    setHauteur(height * echelle);
  }, [section, largeur]);

  return (
    <div
      ref={hote}
      data-testid="vignette-docx"
      style={{
        width: largeur,
        // Proportion d'une page A4, le temps que la vraie hauteur soit connue.
        height: hauteur || largeur * Math.SQRT2,
        overflow: 'hidden',
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
      }}
    />
  );
};

interface VignettesDocxProps {
  /** Pages rendues par `docx-preview`, dans l'ordre du document. */
  sections: Array<HTMLElement>;
  /** Page affichée, numérotée à partir de 1. */
  pageCourante: number;
  onPageChange: (numero: number) => void;
  title?: string;
}

/**
 * Colonne des pages d'un DOCX.
 *
 * `docx-preview` rend le document dans le DOM plutôt qu'en images : il n'y a
 * aucune page à passer à `AnnotationSectionPages`, qui affiche des vignettes
 * d'images. On réduit donc les pages déjà rendues, ce qui évite d'embarquer un
 * moteur de rendu HTML vers canvas pour le seul besoin de la colonne.
 */
const VignettesDocx = (props: VignettesDocxProps) => {
  const {
    sections,
    pageCourante,
    onPageChange,
    title = 'PAGE(S) DU DOCUMENT',
  } = props;

  const colonne = React.useRef<HTMLDivElement>(null);
  const [largeur, setLargeur] = React.useState(LARGEUR_MINIMALE);

  // Les vignettes occupent toute la largeur disponible : trop étroites, elles
  // ne donnent aucune idée du contenu de la page.
  React.useEffect(() => {
    const cible = colonne.current;
    if (!cible) return undefined;

    const mesurer = () =>
      setLargeur(
        Math.max(cible.clientWidth - MARGE_VIGNETTE, LARGEUR_MINIMALE),
      );

    mesurer();
    window.addEventListener('resize', mesurer);

    return () => window.removeEventListener('resize', mesurer);
  }, []);

  return (
    <>
      <AnnotationSectionHeader title={title} />
      <Segment
        attached="bottom"
        className="section-pages"
        style={{ flex: 1, overflowY: 'auto', padding: '0.5em' }}
      >
        <div
          ref={colonne}
          data-testid="vignettes-docx"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {_.map(sections, (section, index) => (
            <Message
              // Les pages n'ont pas d'identité propre : leur rang est stable
              // tant que le document ne change pas, et un changement de
              // document remplace la liste entière.
              // biome-ignore lint/suspicious/noArrayIndexKey: cf. ci-dessus
              key={index}
              color={index + 1 === pageCourante ? 'blue' : undefined}
              onClick={() => onPageChange(index + 1)}
              style={{ cursor: 'pointer', padding: '0.5em', width: 'auto' }}
            >
              <Vignette section={section} largeur={largeur} />
              <Segment basic textAlign="center" style={{ padding: '0.3em 0' }}>
                {index + 1}
              </Segment>
            </Message>
          ))}
        </div>
      </Segment>
    </>
  );
};

export default VignettesDocx;
